import { alpha, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { FC } from 'react';

import useAppTheme from '../../../../hooks/useAppTheme';
import { TableSpec } from '../../interfaces';
import { formatValue } from '../../utils/format';

interface InsightTableProps {
  spec: TableSpec;
  // Shade numeric count cells by size — used for the customer map.
  heatmap?: boolean;
}

const InsightTable: FC<InsightTableProps> = ({ spec, heatmap = false }) => {
  const { palette } = useAppTheme();
  const heatColumns = spec.columns.filter((c) => c.format === 'count');
  const max = heatmap
    ? Math.max(1, ...spec.rows.flatMap((r) => heatColumns.map((c) => Number(r[c.key]) || 0)))
    : 1;

  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
      <Table size='small' sx={{ minWidth: 520 }}>
        <TableHead>
          <TableRow>
            {spec.columns.map((c) => (
              <TableCell key={c.key} align={c.align ?? 'left'}
                sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', bgcolor: 'grey.50', whiteSpace: 'nowrap' }}>
                {c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {spec.rows.map((row, i) => (
            <TableRow key={i} hover>
              {spec.columns.map((c) => {
                const raw = row[c.key];
                const numeric = typeof raw === 'number' || (raw !== null && raw !== '' && !Number.isNaN(Number(raw)) && c.format);
                const shade = heatmap && c.format === 'count' ? Math.min(0.85, (Number(raw) || 0) / max) : 0;
                return (
                  <TableCell key={c.key} align={c.align ?? 'left'}
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: c.align === 'left' || !c.format ? 500 : 400,
                      whiteSpace: 'nowrap',
                      ...(shade ? { bgcolor: alpha(palette.primary.main, shade * 0.6), color: shade > 0.55 ? 'common.white' : 'text.primary' } : {}),
                    }}>
                    <Box component='span'>{numeric && c.format ? formatValue(Number(raw), c.format) : (raw ?? '—')}</Box>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default InsightTable;
